/**
 * @file feed-model.ts
 * @description Строки ленты диалога (chat-window-design.md §3): разделители
 * дней, линия «Новые сообщения», пузыри клиента / мои / коллег, служебные
 * записи и их сворачивание в «· 3 служебные записи ·».
 *
 * Чистая функция: лента от сервера + неотправленные сообщения + выбор
 * «служебные показаны / свёрнуты» → список строк для отрисовки. Свёрнутые
 * подряд идущие записи становятся одной тонкой строкой-группой, нажатие
 * раскрывает именно эту группу (`expandedGroups`).
 */

import type { ChatFeedItem, ChatMessageDto, ConversationEventDto } from "../../lib/api-chat";
import { dayKey, dayTitle } from "./chat-format";
import type { PendingMessage } from "./useMessageSender";

export type BubbleSide = "customer" | "mine" | "colleague" | "system";

export type FeedRow =
  | { type: "day"; key: string; title: string }
  | { type: "unreadLine"; key: string }
  | { type: "message"; key: string; message: ChatMessageDto; side: BubbleSide; showAuthor: boolean }
  | { type: "pending"; key: string; pending: PendingMessage }
  | { type: "event"; key: string; event: ConversationEventDto; highlight: boolean }
  | { type: "eventGroup"; key: string; events: ConversationEventDto[] };

export interface FeedRowOptions {
  firstUnreadId: string | null;
  serviceShown: boolean;
  expandedGroups: ReadonlySet<string>;
  /** Запись о передаче мне — подсвечена у получателя. */
  highlightEventId: string | null;
  now?: Date;
}

export function bubbleSide(m: ChatMessageDto): BubbleSide {
  if (m.authorType === "CUSTOMER") return "customer";
  if (m.authorType === "SYSTEM") return "system";
  return m.isMine ? "mine" : "colleague";
}

type Entry =
  | { kind: "message"; at: string; message: ChatMessageDto }
  | { kind: "event"; at: string; event: ConversationEventDto }
  | { kind: "pending"; at: string; pending: PendingMessage };

export function buildFeedRows(
  items: readonly ChatFeedItem[],
  pending: readonly PendingMessage[],
  options: FeedRowOptions,
): FeedRow[] {
  const now = options.now ?? new Date();
  const entries: Entry[] = [...items, ...pending.map((p) => ({ kind: "pending" as const, at: p.createdAt, pending: p }))];
  const rows: FeedRow[] = [];
  let lastDay = "";
  let prevAuthor: string | null = null;
  let group: ConversationEventDto[] = [];

  const flushGroup = () => {
    if (group.length === 0) return;
    const key = `g:${group[0]?.id ?? ""}`;
    if (options.expandedGroups.has(key)) {
      group.forEach((e) => rows.push({ type: "event", key: `e:${e.id}`, event: e, highlight: e.id === options.highlightEventId }));
    } else {
      rows.push({ type: "eventGroup", key, events: group });
    }
    group = [];
  };

  for (const entry of entries) {
    const day = dayKey(entry.at);
    if (day !== lastDay) {
      flushGroup();
      rows.push({ type: "day", key: `d:${day}`, title: dayTitle(entry.at, now) });
      lastDay = day;
      prevAuthor = null;
    }
    if (entry.kind === "event") {
      prevAuthor = null;
      if (options.serviceShown) {
        rows.push({ type: "event", key: `e:${entry.event.id}`, event: entry.event, highlight: entry.event.id === options.highlightEventId });
      } else group.push(entry.event);
      continue;
    }
    flushGroup();
    if (entry.kind === "pending") {
      rows.push({ type: "pending", key: entry.pending.tempId, pending: entry.pending });
      prevAuthor = "me";
      continue;
    }
    const m = entry.message;
    if (m.id === options.firstUnreadId) rows.push({ type: "unreadLine", key: "unread" });
    const side = bubbleSide(m);
    const author = side === "colleague" ? (m.authorUserId ?? "colleague") : side;
    rows.push({ type: "message", key: `m:${m.id}`, message: m, side, showAuthor: side === "colleague" && author !== prevAuthor });
    prevAuthor = author;
  }
  flushGroup();
  return rows;
}

/** Служебных записей в ленте — число на переключателе. */
export function countServiceRecords(items: readonly ChatFeedItem[]): number {
  return items.filter((i) => i.kind === "event").length;
}
